<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import FilterBar from '@lib/components/shared/FilterBar.vue'
import BInput from '@lib/components/base/BInput.vue'
import BSelect from '@lib/components/base/BSelect.vue'

interface FilterModel {
    search: string
    event: string | null
    ticketset: string | null
    used: boolean | null
}

const props = defineProps<{
    modelValue: FilterModel
    eventOptions?: Array<{ id: string; name: string }>
    ticketsetOptions?: Array<{ id: string; name: string }>
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', v: FilterModel): void
    (e: 'clear'): void
}>()

const { t } = useI18n()

const usedOptions = computed(() => [
    { value: null, label: t('tickets.filters.usedAny') },
    { value: true, label: t('tickets.filters.usedYes') },
    { value: false, label: t('tickets.filters.usedNo') }
])

const activeCount = computed(() => {
    let n = 0
    if (props.modelValue.search) n++
    if (props.modelValue.event) n++
    if (props.modelValue.ticketset) n++
    if (props.modelValue.used !== null) n++
    return n
})

function update<K extends keyof FilterModel>(key: K, value: FilterModel[K]) {
    emit('update:modelValue', { ...props.modelValue, [key]: value })
}
</script>

<template>
    <FilterBar :active-count="activeCount" @clear="$emit('clear')">
        <label class="block">
            <span class="text-xs text-gray-600 mb-1 block">{{ t('tickets.filters.search') }}</span>
            <BInput
                :model-value="modelValue.search"
                :placeholder="t('tickets.filters.search')"
                @update:model-value="v => update('search', v)"
            />
        </label>
        <label class="block">
            <span class="text-xs text-gray-600 mb-1 block">{{ t('tickets.filters.event') }}</span>
            <BSelect
                :model-value="modelValue.event"
                :options="eventOptions ?? []"
                option-label="name"
                option-value="id"
                :placeholder="t('tickets.filters.event')"
                :filter="true"
                @update:model-value="v => update('event', v as string | null)"
            />
        </label>
        <label class="block">
            <span class="text-xs text-gray-600 mb-1 block">{{ t('tickets.filters.ticketset') }}</span>
            <BSelect
                :model-value="modelValue.ticketset"
                :options="ticketsetOptions ?? []"
                option-label="name"
                option-value="id"
                :placeholder="t('tickets.filters.ticketset')"
                :filter="true"
                @update:model-value="v => update('ticketset', v as string | null)"
            />
        </label>
        <label class="block">
            <span class="text-xs text-gray-600 mb-1 block">{{ t('tickets.filters.used') }}</span>
            <BSelect
                :model-value="modelValue.used"
                :options="usedOptions"
                option-label="label"
                option-value="value"
                :placeholder="t('tickets.filters.usedAny')"
                :show-clear="false"
                @update:model-value="v => update('used', v as boolean | null)"
            />
        </label>
    </FilterBar>
</template>
